/**
 * O arraste das linhas de lançamento, por Pointer Events com captura.
 *
 * Papel: reproduz `initDragDrop` (l. 5474–5562): o arraste começa só na alça,
 * que captura o ponteiro — e por isso continua recebendo o movimento e o
 * soltar mesmo com o mouse rápido ou fora da janela —; a linha de origem fica
 * a 40 %, o `<body>` perde a seleção de texto e ganha a mão fechada, um rótulo
 * segue o ponteiro a 14 px, e a linha sob o ponteiro ganha o fundo do arraste.
 * Soltar na metade de cima de uma linha põe antes dela; na de baixo, depois.
 *
 * Como usar: a tabela passa `handleAgarrar` à alça de cada linha e lê
 * `idArrastado` e `idSobre` para pintar as duas linhas; quem monta a tabela
 * desenha o `fantasma` e recebe o soltar em `onSoltar`. Soltar fora de uma
 * linha, ou na própria, não chama nada. O gancho não sabe de mês: quem recebe
 * o soltar recusa a linha de outro mês, como `reorderLanc` (l. 2711).
 *
 * A linha sob o ponteiro é achada por `document.elementFromPoint` e pelo
 * `data-lancamento-id` da linha. O rótulo tem `pointer-events:none`, e por isso
 * não atrapalha a busca.
 */
import { type LancamentoDaListagem } from '@concierge/shared';
import {
  type PointerEvent as EventoDoReact,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { valorDaLinha } from '../formato';

/** O rótulo que segue o ponteiro. */
export interface FantasmaDoArraste {
  /** A posição do canto de cima, já com os 14 px de folga. */
  x: number;
  y: number;
  texto: string;
}

/** O estado do arraste e o começo dele. */
export interface ArrasteDeLinhas {
  /** A linha que está sendo arrastada. */
  idArrastado: string | null;
  /** A linha sob o ponteiro, que não é a arrastada. */
  idSobre: string | null;
  fantasma: FantasmaDoArraste | null;
  /** O aperto da alça de uma linha. */
  handleAgarrar: (evento: EventoDoReact<HTMLElement>, linha: LancamentoDaListagem) => void;
}

/** A folga entre o ponteiro e o rótulo (l. 5502–5503). */
const FOLGA = 14;

/** As duas classes que o `<body>` ganha durante o arraste (`body.dragging-active`). */
const CLASSES_DO_CORPO = ['cursor-grabbing', 'select-none'];

/**
 * O texto do rótulo de uma linha, como a origem o monta das células.
 *
 * @param linha - A linha arrastada.
 * @returns "⠿ Favorecido — -R$ 100,00", ou "⠿ Lançamento — …" sem favorecido.
 */
function textoDoFantasma(linha: LancamentoDaListagem): string {
  const nome = linha.favorecido?.nome ?? '';
  return `⠿ ${nome === '' ? 'Lançamento' : nome} — ${valorDaLinha(linha)}`;
}

/**
 * Liga o arraste às linhas de uma tabela.
 *
 * @param onSoltar - Recebe a linha arrastada, a linha de destino e se o
 *   destino é depois dela.
 * @returns O estado e o começo do arraste.
 */
export function useArrasteDeLinhas(
  onSoltar: (origemId: string, alvoId: string, depois: boolean) => void,
): ArrasteDeLinhas {
  const [estado, setEstado] = useState<{
    idArrastado: string;
    idSobre: string | null;
    fantasma: FantasmaDoArraste;
  } | null>(null);
  const soltar = useRef(onSoltar);
  const terminarAtivo = useRef<(() => void) | null>(null);

  useEffect(() => {
    soltar.current = onSoltar;
  });

  // Desmontar no meio do arraste deixaria o `<body>` com a mão fechada e os
  // ouvintes presos à alça.
  useEffect(() => () => terminarAtivo.current?.(), []);

  const handleAgarrar = useCallback(
    (evento: EventoDoReact<HTMLElement>, linha: LancamentoDaListagem) => {
      const alca = evento.currentTarget;
      alca.setPointerCapture(evento.pointerId);
      evento.preventDefault();

      const origemId = linha.id;
      const texto = textoDoFantasma(linha);
      document.body.classList.add(...CLASSES_DO_CORPO);
      setEstado({
        idArrastado: origemId,
        idSobre: null,
        fantasma: { x: evento.clientX + FOLGA, y: evento.clientY + FOLGA, texto },
      });

      /**
       * A linha sob o ponteiro e a metade em que ele está.
       *
       * @param x - A coordenada horizontal do ponteiro.
       * @param y - A vertical.
       * @returns O destino, ou nulo fora de uma linha ou na própria.
       */
      function destino(x: number, y: number): { id: string; depois: boolean } | null {
        const linhaSob = document
          .elementFromPoint(x, y)
          ?.closest<HTMLElement>('tr[data-lancamento-id]');
        const id = linhaSob?.dataset.lancamentoId;
        if (linhaSob == null || id === undefined || id === origemId) return null;
        const caixa = linhaSob.getBoundingClientRect();
        return { id, depois: y > caixa.top + caixa.height / 2 };
      }

      /**
       * Leva o rótulo e o destaque junto com o ponteiro.
       *
       * @param movimento - O `pointermove` capturado.
       */
      function handleMover(movimento: PointerEvent) {
        setEstado({
          idArrastado: origemId,
          idSobre: destino(movimento.clientX, movimento.clientY)?.id ?? null,
          fantasma: { x: movimento.clientX + FOLGA, y: movimento.clientY + FOLGA, texto },
        });
      }

      /** Desfaz tudo o que o arraste pôs na tela. */
      function handleTerminar() {
        alca.removeEventListener('pointermove', handleMover);
        alca.removeEventListener('pointerup', handleLargar);
        alca.removeEventListener('pointercancel', handleTerminar);
        alca.removeEventListener('lostpointercapture', handleTerminar);
        document.body.classList.remove(...CLASSES_DO_CORPO);
        terminarAtivo.current = null;
        setEstado(null);
      }

      /**
       * Solta a linha onde o ponteiro está.
       *
       * @param soltura - O `pointerup` capturado.
       */
      function handleLargar(soltura: PointerEvent) {
        const alvo = destino(soltura.clientX, soltura.clientY);
        handleTerminar();
        if (alvo !== null) soltar.current(origemId, alvo.id, alvo.depois);
      }

      alca.addEventListener('pointermove', handleMover);
      alca.addEventListener('pointerup', handleLargar);
      alca.addEventListener('pointercancel', handleTerminar);
      alca.addEventListener('lostpointercapture', handleTerminar);
      terminarAtivo.current = handleTerminar;
    },
    [],
  );

  return {
    idArrastado: estado?.idArrastado ?? null,
    idSobre: estado?.idSobre ?? null,
    fantasma: estado?.fantasma ?? null,
    handleAgarrar,
  };
}
